import React, { useEffect, useState } from "react";
import { Container, Row, Col, Card, Button, Badge, Nav } from "react-bootstrap";
import { motion } from "framer-motion";
import {
  FaFacebookF, FaInstagram, FaWhatsapp, FaYoutube, FaTiktok, FaXTwitter, FaLinkedinIn,
  FaGooglePlay, FaApple, FaTv, FaGlobe, FaEnvelope
} from "react-icons/fa6";

/**
 * SocialHub.jsx
 * Página moderna e responsiva de links para redes sociais e apps.
 *
 * Requisitos: 
 * 1) Instale dependências:
 *    npm i react-bootstrap bootstrap framer-motion react-icons
 *
 * 2) Garanta o CSS base do Bootstrap no seu entrypoint (ex.: src/main.jsx ou src/index.js):
 *    import 'bootstrap/dist/css/bootstrap.min.css';
 *
 * 3) Use: <SocialHub brandName="Gigabyte Telecom" primaryColor="#7C3AED" accentColor="#EC4899" />
 */

const MotionCard = motion(Card);

export default function SocialHub({ brandName = "Gigabyte Telecom", primaryColor = "#7C3AED", accentColor = "#EC4899" }) {

  const message = "Internet, TV por Assinatura, Telefonia Fixa, Internet Rural";
  const [whatsAppLink, setWhatsAppLink] = useState("");

  
  useEffect(() => {
    const userAgent = navigator.userAgent.toLowerCase();
    const baseURL = "https://wa.me/556222000000";
    const messageParam = encodeURIComponent(`Olá, entre para falar sobre: ${message}`);
    
    if (/android|webos|iphone|ipad|ipod|blackberry|iemobile|opera mini/.test(userAgent)) {
      setWhatsAppLink(`whatsapp://send?phone=556222000000&text=${messageParam}`);
    } else {
      setWhatsAppLink(`${baseURL}?text=${messageParam}`);
    }
  }, [message]);

  const links = [
    {
      group: "Redes Sociais",
      items: [
        { label: "Instagram", url: "https://www.instagram.com/gigabytetelecom", icon: <FaInstagram />, tag: "@gigabytetelecom" },
        { label: "Facebook", url: "https://www.facebook.com/gigabytetelecom", icon: <FaFacebookF />, tag: "/gigabytetelecom" },
        { label: "WhatsApp Comercial", url: `${ whatsAppLink }`, icon: <FaWhatsapp />, tag: "556222000000" },
        // { label: "YouTube", url: "https://www.youtube.com/@gigabytetelecom6276", icon: <FaYoutube />, tag: "@gigabytetelecom" },
        // { label: "TikTok", url: "https://tiktok.com/@gigabytetelecom", icon: <FaTiktok />, tag: "@gigabytetelecom" },
        // { label: "X (Twitter)", url: "https://x.com/gigabytetelecom", icon: <FaXTwitter />, tag: "@gigabytetelecom" },
        { label: "LinkedIn", url: "https://www.linkedin.com/company/gigabyte-telecom", icon: <FaLinkedinIn />, tag: "/company/gigabytetelecom" },
      ],
    },
    {
      group: "Apps para TV",
      items: [
        { label: "Android TV / Google TV", url: "https://play.google.com/store/apps/details?id=com.gigabytemobile&hl=pt_BR&pli=1", icon: <FaGooglePlay />, tag: "Google Play" },
        { label: "Apple TV", url: "https://apps.apple.com/br/app/gigabyte-play/id6443457059?l=pt", icon: <FaApple />, tag: "App Store" },
        { label: "LG webOS", url: "https://br.lgappstv.com/main/integrated/search?searchText=Gigabyte%20Play", icon: <FaTv />, tag: "Download" },
        { label: "Samsung Tizen", url: "https://play.google.com/store/apps/details?id=com.gigabytemobile&hl=pt_BR&pli=1", icon: <FaTv />, tag: "Download" },
      ],
    },
    {
      group: "App do Cliente",
      items: [
        { label: "Android (Cliente)", url: "https://play.google.com/store/apps/details?id=br.com.badbit.gigabytetelecom&hl=pt_BR", icon: <FaGooglePlay />, tag: "Google Play" },
        { label: "iOS (Cliente)", url: "https://apps.apple.com/br/app/gigabyte-telecom-cliente/id1620548702", icon: <FaApple />, tag: "App Store" },
        { label: "Portal do Cliente", url: "http://sac.gigabytetelecom.com.br/", icon: <FaGlobe />, tag: "Acessar" },
        { label: "Suporte por E-mail", url: "mailto:atendimento@gigabytetelecom.com.br", icon: <FaEnvelope />, tag: "suporte@suaempresa.com.br" },
      ],
    },
  ];

  return (
    <div style={{
      minHeight: "100dvh",
      background: `radial-gradient(1200px 600px at 10% 10%, ${hex2rgba(primaryColor, 0.25)}, transparent 60%), radial-gradient(1000px 500px at 90% 20%, ${hex2rgba(accentColor, 0.25)}, transparent 60%), linear-gradient(180deg, #0B1020, #0B0F17)`,
      color: "#E5E7EB",
    }}>
      {/* <TopNav brandName={brandName} primaryColor={primaryColor} /> */}

      <Container className="py-5">
        <Hero brandName={brandName} primaryColor={primaryColor} accentColor={accentColor} />

        {links.map((section) => (
          <section key={section.group} className="mb-4">
            <Row className="g-4">
              <Col xs={12}>
                <Badge bg="" style={{
                  background: hex2rgba(primaryColor, 0.2),
                  color: "#fff",
                  border: `1px solid ${hex2rgba(primaryColor, 0.4)}`,
                  padding: "0.6rem 1rem",
                  fontSize: "0.9rem",
                  borderRadius: "999px",
                  letterSpacing: 0.5,
                }}>{section.group}</Badge>
              </Col>

              {section.items.map((item, idx) => (
                <Col key={item.label} xs={12} sm={6} lg={4} xxl={3}>
                  <MotionCard
                    whileHover={{ y: -6, scale: 1.01 }}
                    whileTap={{ scale: 0.99 }}
                    transition={{ type: "spring", stiffness: 260, damping: 20 }}
                    className="h-100 shadow"
                    style={{
                      background: "rgba(255,255,255,0.06)",
                      border: `1px solid ${hex2rgba("#FFFFFF", 0.12)}`,
                      borderRadius: "1.25rem",
                      backdropFilter: "blur(8px)",
                    }}
                  >
                    <Card.Body className="d-flex flex-column">
                      <div className="d-flex align-items-center gap-3 mb-3">
                        <div style={{
                          width: 48, height: 48, borderRadius: 12,
                          display: "grid", placeItems: "center",
                          // background: `#000`,
                          background: `linear-gradient(135deg, ${hex2rgba(primaryColor, 0.25)}, ${hex2rgba(accentColor, 0.25)})`,
                          border: `1px solid ${hex2rgba("#FFFFFF", 0.16)}`,
                        }}>
                          <i style={{ fontSize: 22, lineHeight: 0, color: "#FFFFFF" }}>{item.icon}</i>
                        </div>
                        <div className="flex-grow-1">
                          <Card.Title as="h3" className="h5 m-0" style={{ color: "#F9FAFB" }}>{item.label}</Card.Title>
                          <Card.Subtitle className="text-muted" style={{ color: "#A8B3CF" }}>{item.tag}</Card.Subtitle>
                        </div>
                      </div>

                      <div className="mt-auto d-grid">
                        <Button
                          as="a"
                          href={item.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          size="lg"
                          style={{
                            borderRadius: 12,
                            border: 0,
                            background: `linear-gradient(135deg, ${primaryColor}, ${accentColor})`,
                            boxShadow: `0 8px 24px ${hex2rgba(accentColor, 0.35)}`,
                          }}
                        >
                          Acessar
                        </Button>
                      </div>
                    </Card.Body>
                  </MotionCard>
                </Col>
              ))}
            </Row>
          </section>
        ))}

        <footer className="py-4 text-center" style={{ opacity: 0.8 }}>
          <small>
            © {new Date().getFullYear()} {brandName}. Todos os Apps com direitos reservados.
          </small>
        </footer>

      </Container>
    </div>
  );
}

function TopNav({ brandName, primaryColor }) {
  return (
    <Nav className="py-3" as="nav" style={{
      borderBottom: `1px solid ${hex2rgba("#FFFFFF", 0.08)}`,
      background: "transparent",
    }}>
      <Container className="d-flex align-items-center justify-content-between">
        <div className="d-flex align-items-center gap-2">
          <span style={{
            width: 12, height: 12, borderRadius: 999,
            display: "inline-block",
            background: primaryColor,
            boxShadow: `0 0 0 4px ${hex2rgba(primaryColor, 0.25)}`,
          }} />
          <strong style={{ letterSpacing: 0.3 }}>{brandName}</strong>
        </div>
        <div className="d-none d-md-flex gap-3">
          <Nav.Link href="#social" className="text-light-50">Redes</Nav.Link>
          <Nav.Link href="#tv" className="text-light-50">TV</Nav.Link>
          <Nav.Link href="#apps" className="text-light-50">Apps</Nav.Link>
        </div>
      </Container>
    </Nav>
  );
}

function Hero({ brandName, primaryColor, accentColor }) {
  return (
    <div className="text-center mb-5">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
      >
        <Badge bg="" style={{
          background: hex2rgba("#FFFFFF", 0.06),
          color: "#E5E7EB",
          border: `1px solid ${hex2rgba("#FFFFFF", 0.12)}`,
          borderRadius: 999,
          padding: "0.6rem 1rem",
          letterSpacing: 0.6,
        }}>
          Sua central de links
        </Badge>

        <h1 className="display-5 fw-bold mt-3" style={{ color: "#F3F4F6" }}>
          Conecte-se com <span style={{ background: `linear-gradient(90deg, ${primaryColor}, ${accentColor})`, WebkitBackgroundClip: "text", color: "transparent" }}>{brandName}</span>
        </h1>
        <p className="lead" style={{ color: "#A8B3CF", maxWidth: 760, margin: "0 auto" }}>
          Acesse rapidamente nossas redes sociais, apps para TV e o app/portal do cliente — tudo em um só lugar.
        </p>
      </motion.div>
    </div>
  );
}

// Utils
function hex2rgba(hex, alpha = 1) {
  const parsed = hex.replace('#', '');
  const bigint = parseInt(parsed.length === 3
    ? parsed.split('').map(c => c + c).join('')
    : parsed, 16);
  const r = (bigint >> 16) & 255;
  const g = (bigint >> 8) & 255;
  const b = bigint & 255;
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}
